/*
 * KAPSTAT — study runner: [invite gate] → [eligibility screen] → consent →
 * sections → [education → post] → done. With autosave/resume and dedup.
 * Copyright (c) 2026 Eddlynn Edtech Private Limited. All rights reserved.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SECTION_META, SECTION_SEQUENCE, ROLES, roleLabel, ETHICS } from './studies/common';
import { ensureSession, saveParticipant, submitPhase, logEducation, isConfigured } from './lib/db';
import {
  getStudySettings, redeemCode, hasFinalSubmission, eligibleStudies,
  loadWip, saveWip, clearWip, markDone, isDoneLocal,
  saveCodeFlag, getCodeFlag, saveScreenFlag, getScreenFlag,
} from './lib/gating';
import { ProgressBar, Field, Modal } from './ui.jsx';
import { Wordmark } from './Wordmark.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';
import { SubscribeCTA } from './Subscribe.jsx';
import { FeedbackPrompt } from './Feedback.jsx';

const now = () => Date.now();
// Section flow is derived per study from the sections it actually declares (in the
// canonical SECTION_SEQUENCE order). demographics is baseline-only; every other
// non-empty section repeats in the post phase.
const preSteps = (study) => SECTION_SEQUENCE.filter((k) => (study.sections[k] || []).length);
const postSteps = (study) => preSteps(study).filter((k) => k !== 'demographics');
const reasonText = (r) => r === 'used_up' ? 'This code has already been used.' : r === 'invalid' ? 'That code is not valid for this study.' : 'Could not verify the code. Please try again.';

export default function StudyRunner({ study }) {
  const isPrePost = study.mode === 'pre-post';
  const finalPhase = isPrePost ? 'post' : 'cross';
  const PRE = preSteps(study);
  const POST = postSteps(study);
  const [searchParams] = useSearchParams();

  const [uid, setUid] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading|gate|screen|rejected|already|consent|pre|education|post|done
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState(0);
  const [pre, setPre] = useState({});
  const [post, setPost] = useState({});
  const [groupTag, setGroupTag] = useState(null);
  const [eligibleRoles, setEligibleRoles] = useState(null);
  const [screenRole, setScreenRole] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [resumed, setResumed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [startedAt, setStartedAt] = useState(0);
  const [codeInput, setCodeInput] = useState('');
  const [codeErr, setCodeErr] = useState(null);
  // education engagement gate
  const [eduIntro, setEduIntro] = useState(true);
  const [eduConfirm, setEduConfirm] = useState(false);
  const [eduUnderstood, setEduUnderstood] = useState(false);
  const [readStartAt, setReadStartAt] = useState(0);
  const [dwellLeft, setDwellLeft] = useState(0);
  const [invalidKeys, setInvalidKeys] = useState([]);
  const [shared, setShared] = useState(false);
  const [singleResponse, setSingleResponse] = useState(true);

  // ---- init: session, dedup, resume, gate, screen ----
  useEffect(() => { (async () => {
    try {
      const id = await ensureSession(); setUid(id);
      const st = await getStudySettings(study.slug);
      const roles = (st.eligible_roles && st.eligible_roles.length) ? st.eligible_roles : null;
      setEligibleRoles(roles);
      setSingleResponse(st.single_response !== false);
      if (isDoneLocal(id, study.slug) || (st.single_response && await hasFinalSubmission(study.slug, finalPhase))) { setPhase('already'); return; }
      const wip = loadWip(id, study.slug);
      if (wip) {
        setPre(wip.pre || {}); setPost(wip.post || {}); setStep(wip.step || 0);
        setGroupTag(wip.groupTag || null); setScreenRole(wip.screenRole || null);
        setStartedAt(now()); setResumed(true); setPhase(wip.phase || 'pre');
        return;
      }
      const afterAccess = () => {
        if (roles && !getScreenFlag(id, study.slug)) setPhase('screen');
        else { const f = getScreenFlag(id, study.slug); if (f && f !== '1') setScreenRole(f); setPhase('consent'); }
      };
      const existing = getCodeFlag(id, study.slug);
      if (st.gated && !existing) {
        const urlCode = searchParams.get('c');
        if (urlCode) {
          const res = await redeemCode(urlCode, study.slug);
          if (res.ok) { saveCodeFlag(id, study.slug, res.group_tag); if (res.group_tag) setGroupTag(res.group_tag); afterAccess(); }
          else { setCodeErr(reasonText(res.reason)); setPhase('gate'); }
        } else setPhase('gate');
      } else {
        if (existing && existing !== '1') setGroupTag(existing);
        afterAccess();
      }
    } catch (e) { setErr(String(e)); setPhase('consent'); }
  })(); }, [study.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- autosave ----
  useEffect(() => {
    if (!uid) return;
    if (phase === 'pre' || phase === 'education' || phase === 'post') {
      saveWip(uid, study.slug, { phase, step, pre, post, groupTag, screenRole });
    }
  }, [uid, phase, step, pre, post, groupTag, screenRole, study.slug]);

  // ---- education read-gate (minimum dwell + live countdown) ----
  const eduPoints = (study.education || []).reduce((n, m) => n + (m.points ? m.points.length : 0), 0);
  const minDwell = Math.min(Math.max(5, Math.round(eduPoints * 1.2)), 12);
  useEffect(() => {
    if (phase !== 'education' || eduIntro || dwellLeft <= 0) return;
    const t = setTimeout(() => setDwellLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [phase, eduIntro, dwellLeft]);

  const steps = phase === 'post' ? POST : PRE;
  const sectionKey = (phase === 'pre' || phase === 'post') ? steps[step] : null;
  const section = sectionKey ? { ...SECTION_META[sectionKey], items: study.sections[sectionKey] || [] } : null;
  const answers = phase === 'post' ? post : pre;
  const setAnswers = phase === 'post' ? setPost : setPre;
  const setAnswer = (key, value) => { setAnswers((a) => ({ ...a, [key]: value })); setInvalidKeys((ks) => (ks.length ? ks.filter((k) => k !== key) : ks)); };
  const unanswered = () => (!section ? [] : section.items.filter((it) => it.required !== false && (answers[it.key] === undefined || answers[it.key] === '' || answers[it.key] === null)).map((it) => it.key));
  const totalUnits = isPrePost ? PRE.length + 1 + POST.length : PRE.length;
  const journeyUnit = () => (phase === 'pre' ? step : phase === 'education' ? PRE.length : phase === 'post' ? PRE.length + 1 + step : totalUnits);

  function finish() { markDone(uid, study.slug); clearWip(uid, study.slug); }

  async function submitCode(e) {
    e.preventDefault(); setCodeErr(null); setBusy(true);
    try {
      const res = await redeemCode(codeInput, study.slug);
      if (res.ok) {
        saveCodeFlag(uid, study.slug, res.group_tag); if (res.group_tag) setGroupTag(res.group_tag);
        if (eligibleRoles && !getScreenFlag(uid, study.slug)) setPhase('screen'); else setPhase('consent');
        window.scrollTo(0, 0);
      } else setCodeErr(reasonText(res.reason));
    } catch (ex) { setCodeErr(String(ex.message || ex)); }
    finally { setBusy(false); }
  }

  async function submitScreen() {
    if (!screenRole) return;
    if (eligibleRoles.includes(screenRole)) {
      saveScreenFlag(uid, study.slug, screenRole); setPhase('consent'); window.scrollTo(0, 0);
    } else {
      setBusy(true);
      try { setSuggestions(await eligibleStudies(screenRole, study.slug)); } catch (e) { console.warn('eligibleStudies failed', e); }
      setBusy(false); setPhase('rejected'); window.scrollTo(0, 0);
    }
  }

  function startSurvey() { setErr(null); setPhase('pre'); setStep(0); setStartedAt(now()); window.scrollTo(0, 0); }

  async function next() {
    const miss = unanswered();
    if (miss.length) {
      setInvalidKeys(miss);
      setErr(`Please answer the highlighted question${miss.length > 1 ? 's' : ''} to continue.`);
      setTimeout(() => document.getElementById('lbl-' + miss[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
      return;
    }
    setInvalidKeys([]); setErr(null);
    if (step < steps.length - 1) { setStep(step + 1); window.scrollTo(0, 0); return; }
    setBusy(true);
    try {
      // Re-verify just before a final submit: catches a duplicate that landed
      // after this page loaded (e.g. the same person submitted in another tab,
      // or returned on this device meanwhile). Dedup is otherwise only checked
      // on load, so without this the second copy is silently dropped by the DB.
      const finalSubmitNow = phase === 'post' || (phase === 'pre' && !isPrePost);
      if (finalSubmitNow && singleResponse
          && (isDoneLocal(uid, study.slug) || await hasFinalSubmission(study.slug, finalPhase))) {
        markDone(uid, study.slug); clearWip(uid, study.slug);
        setPhase('already'); window.scrollTo(0, 0); return;
      }
      if (phase === 'pre') {
        const fields = {};
        (study.sections.demographics || []).forEach((d) => { if (!d.field) return; let v = pre[d.key]; if (d.field === 'age') v = Number(v); fields[d.field] = v; });
        const effGroup = groupTag || screenRole;
        if (effGroup) fields.group_tag = effGroup;
        await saveParticipant(uid, study.slug, fields);
        await submitPhase(uid, study.slug, isPrePost ? 'pre' : 'cross', pre, Math.round((now() - startedAt) / 1000), study.version);
        if (isPrePost) { setPhase('education'); setEduIntro(true); setEduConfirm(false); setEduUnderstood(false); setDwellLeft(0); } else { finish(); setPhase('done'); }
        window.scrollTo(0, 0);
      } else {
        await submitPhase(uid, study.slug, 'post', post, Math.round((now() - startedAt) / 1000), study.version);
        finish(); setPhase('done'); window.scrollTo(0, 0);
      }
    } catch (e) { setErr('Could not save right now — please check your connection and tap the button again. Your answers are kept.'); console.warn('submit failed', e); }
    finally { setBusy(false); }
  }

  function back() { if (step > 0) { setStep(step - 1); setErr(null); setInvalidKeys([]); window.scrollTo(0, 0); } }
  function beginReading() { setEduIntro(false); setReadStartAt(now()); setDwellLeft(minDwell); window.scrollTo(0, 0); }
  async function finishEducation() {
    setEduConfirm(false);
    try { await logEducation(uid, study.slug, 'module-set', Math.round((now() - (readStartAt || startedAt)) / 1000)); } catch (e) { console.warn('logEducation failed', e); }
    setPhase('post'); setStep(0); setStartedAt(now()); window.scrollTo(0, 0);
  }
  function shareStudy() {
    const url = `${window.location.origin}/study/${study.slug}`;
    if (navigator.share) navigator.share({ title: study.studyTitle, url }).catch(() => {});
    else if (navigator.clipboard) navigator.clipboard.writeText(url).then(() => setShared(true)).catch(() => setShared(true));
    else setShared(true);
  }

  return (
    <div className="app" style={{ '--ca': study.accent }}>
      <div className="topbar">
        <Link to="/" className="topbar__home"><Wordmark height={20} /></Link>
        <div className="topbar__study">{study.shortTitle}</div>
        <ThemeToggle />
      </div>

      {!isConfigured && <div className="demo-banner">Demo mode — responses are saved only in this browser. Configure Supabase to record real data.</div>}
      {err && <div className="error">{err}</div>}

      {phase === 'loading' && <div className="card"><p className="muted">Loading…</p></div>}

      {phase === 'already' && (
        <div className="card center">
          <div className="big-check">✓</div>
          <h1>You’ve already responded</h1>
          <p className="muted">Our records show this study has already been completed on this device. Thank you — each person responds once.</p>
          <div className="btn-row" style={{ justifyContent: 'center' }}><Link className="btn btn--ghost" to="/studies">Other studies</Link></div>
        </div>
      )}

      {phase === 'gate' && (
        <div className="card">
          <span className="pill">By invitation</span>
          <h1>{study.studyTitle}</h1>
          <p className="muted">This study is open to invited participants only. Enter the access code you were given (or use your invite link).</p>
          <form onSubmit={submitCode}>
            <input className="num" style={{ maxWidth: 280, textTransform: 'uppercase' }} value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Access code" autoCapitalize="characters" />
            <div className="btn-row">
              <Link className="btn btn--ghost" to="/studies">Back</Link>
              <button className="btn btn--primary" type="submit" disabled={busy || !codeInput.trim()}>{busy ? 'Checking…' : 'Continue'}</button>
            </div>
          </form>
          {codeErr && <div className="error">{codeErr}</div>}
        </div>
      )}

      {phase === 'screen' && (
        <div className="card">
          <span className="pill">Quick check</span>
          <h1>Are you eligible for this study?</h1>
          <p className="muted">This study is intended for <b>{eligibleRoles ? eligibleRoles.map(roleLabel).join(', ') : 'specific participants'}</b>. Tell us which best describes you — it takes one tap.</p>
          <Field item={{ key: '_role', type: 'single', options: ROLES, stem: 'Which best describes you?' }} value={screenRole} onChange={setScreenRole} />
          <div className="btn-row">
            <Link className="btn btn--ghost" to="/studies">Back</Link>
            <button className="btn btn--primary" disabled={!screenRole || busy} onClick={submitScreen}>{busy ? 'Checking…' : 'Continue'}</button>
          </div>
        </div>
      )}

      {phase === 'rejected' && (
        <div className="card">
          <span className="pill">Not eligible</span>
          <h1>Thanks for your interest!</h1>
          <p>This study is for <b>{eligibleRoles ? eligibleRoles.map(roleLabel).join(', ') : 'specific participants'}</b>, so it isn’t the right fit for “{roleLabel(screenRole)}”. To keep the findings reliable, we can only include eligible participants.</p>
          {suggestions.length > 0 ? (
            <>
              <h3>Studies you can take</h3>
              {suggestions.map((s) => (
                <Link key={s.slug} className="suggest" to={`/study/${s.slug}`}>
                  <span className="suggest__emoji">{s.emoji}</span>
                  <span><b>{s.studyTitle}</b><br /><span className="muted small">{s.topic} · ~{s.estMinutes} min</span></span>
                  <span className="suggest__arrow">→</span>
                </Link>
              ))}
            </>
          ) : <p className="muted">There are no other surveys that match your profile right now — please check back later. Thank you!</p>}
          <div className="btn-row" style={{ justifyContent: 'center' }}><Link className="btn btn--ghost" to="/studies">All studies</Link></div>
        </div>
      )}

      {phase === 'consent' && (
        <div className="card">
          <span className="pill">Anonymous{(groupTag || screenRole) ? ` · ${roleLabel(groupTag || screenRole)}` : ''} · ~{study.estMinutes} min</span>
          <h1>{study.studyTitle}</h1>
          <p>Welcome 👋 — this is a short, <b>completely anonymous</b> survey on <b>KAPSTAT</b>.</p>
          <p className="muted small"><b>KAP</b> stands for <b>Knowledge, Attitude &amp; Practice</b> — a standard way to understand what a group knows, how they feel, and what they actually do about a topic, so education and patient care can be improved where it's needed most.</p>

          <h3>What to expect</h3>
          {isPrePost ? (
            <>
              <div className="edu-point">A few quick questions on {study.shortTitle}</div>
              <div className="edu-point">A 2-minute, guideline-based refresher</div>
              <div className="edu-point">A few follow-up questions — so you don't just get tested, you <b>pick up something useful</b> for the clinic</div>
            </>
          ) : (
            <>
              <div className="edu-point">A few short questions about {study.shortTitle}</div>
              <div className="edu-point">That's the whole thing — about {study.estMinutes} minutes</div>
            </>
          )}

          <div className="privacy-note">
            <span className="privacy-note__icon" aria-hidden="true">🔒</span>
            <p><b>We never ask for your name, email, roll number, or login.</b> Your answers are stored securely and reported only as anonymous group totals — nothing is shared with your faculty, and there are <b>no right-or-wrong consequences</b>.</p>
          </div>

          {isPrePost
            ? <p>You'll come away knowing the current best-practice answers — and because it's anonymous, please just answer honestly.</p>
            : <p>Because it's anonymous, please answer honestly — your candid responses directly help shape better dental education and patient care.</p>}
          <p className="muted small">Participation is voluntary; you can stop any time before submitting, and your progress is saved automatically.</p>

          <details className="consent-details">
            <summary>Your rights, data use &amp; ethics approval</summary>
            <ul>
              <li><b>Who runs this:</b> operated by {ETHICS.controller} (the data controller) for academic research.</li>
              <li><b>What's collected:</b> your answers plus basic demographics (age, sex, year of study). In a small class a rare combination of these could in principle be identifying, so every response is treated as confidential and reported only in grouped totals.</li>
              <li><b>How it's used:</b> anonymous, aggregated results may be published in journals and presentations; individual responses are never shared.</li>
              <li><b>Voluntary &amp; withdrawal:</b> entirely voluntary — stop any time before submitting. Because submitted responses carry no identifier, they cannot be withdrawn afterwards.</li>
              <li><b>Retention:</b> de-identified data is kept for up to {ETHICS.retentionYears} years after publication, then deleted.</li>
              <li><b>Ethics &amp; contact:</b> {ETHICS.iecRef ? `approved by ${ETHICS.iecRef}` : 'conducted under your institution’s research-ethics guidelines'}{ETHICS.contact && <> · questions? <a href={`mailto:${ETHICS.contact}`}>{ETHICS.contact}</a></>}.</li>
            </ul>
          </details>

          <label className="checkbox">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I have read the above (including how my data is used), and I agree to take part.</span>
          </label>
          <div className="btn-row">
            <Link className="btn btn--ghost" to="/studies">Back</Link>
            <button className="btn btn--primary" disabled={!consent || !uid} onClick={startSurvey}>{uid ? 'Start' : 'Loading…'}</button>
          </div>
        </div>
      )}

      {(phase === 'pre' || phase === 'post') && section && (
        <div className="card">
          {resumed && step === 0 && <div className="demo-banner" style={{ background: '#e8f0fb', color: '#1f4e79', borderColor: '#bcd4f0' }}>Welcome back — we resumed where you left off.</div>}
          <div className="step-label">{phase === 'post' ? 'Follow-up' : isPrePost ? 'Baseline' : 'Survey'} — {section.title}</div>
          <ProgressBar value={(journeyUnit() + 1) / totalUnits} />
          <div className="muted small" style={{ marginTop: -10, marginBottom: 12 }}>{isPrePost ? 'Pre → Learn → Post · ' : ''}{Math.round(((journeyUnit() + 1) / totalUnits) * 100)}% complete</div>
          <h2>{section.title}</h2>
          {section.instruction && <p className="muted small">{section.instruction}</p>}
          {section.items.map((it) => <Field key={it.key} item={it} value={answers[it.key]} onChange={(v) => setAnswer(it.key, v)} invalid={invalidKeys.includes(it.key)} />)}
          <div className="btn-row">
            <button className="btn btn--ghost" onClick={back} disabled={step === 0 || busy}>Back</button>
            <button className="btn btn--primary" onClick={next} disabled={busy}>
              {busy ? 'Saving…' : step < steps.length - 1 ? 'Next' : (phase === 'pre' && isPrePost) ? 'Submit & continue' : 'Finish'}
            </button>
          </div>
          <p className="muted small" style={{ marginTop: 12, textAlign: 'center' }}>
            💾 Your progress is saved on this device. If you close this page by mistake, just reopen it on the same device to pick up where you left off.
          </p>
        </div>
      )}

      {phase === 'education' && (
        <>
          <div className="card">
            <span className="pill">Learning module</span>
            <h1>{study.educationTitle || 'Quick learning module'}</h1>
            {study.educationBlurb && <p className="muted">{study.educationBlurb}</p>}
            {(study.education || []).map((m) => (
              <div key={m.id} style={{ marginTop: 16 }}>
                <h3>{m.title}</h3>
                {m.points.map((p, idx) => <div className="edu-point" key={idx}>{p}</div>)}
                {m.tip && (
                  <div className="edu-tip">
                    <span className="edu-tip__icon" aria-hidden="true">💡</span>
                    <span><b>Memory hook — </b>{m.tip}</span>
                  </div>
                )}
              </div>
            ))}
            <div className="edu-gate">
              <button className="btn btn--primary" onClick={() => { setEduConfirm(true); window.scrollTo(0, 0); }} disabled={dwellLeft > 0}
                aria-label={dwellLeft > 0 ? 'Continue to the follow-up — available after a short read' : 'I have finished reading, continue to the follow-up'}>
                {dwellLeft > 0 ? `Keep reading… ${dwellLeft}s` : 'I’ve finished reading →'}
              </button>
              <p className="muted small" style={{ marginTop: 8 }} aria-live="polite">{dwellLeft > 0 ? 'Take a moment — this is exactly what the follow-up asks about.' : ''}</p>
            </div>
          </div>

          {eduIntro && (
            <Modal titleId="eduIntroTitle">
              <div className="modal__emoji" aria-hidden="true">📚</div>
              <h2 id="eduIntroTitle">One quick learning module</h2>
              <p>Please read this short, guideline-based refresher. <b>Right after, you’ll answer the same questions again</b> — so this is your chance to lock in the best-practice answers.</p>
              <p className="muted small">It only takes a couple of minutes. Read it through, then tick the box to continue.</p>
              <div className="btn-row btn-row--end"><button className="btn btn--primary" onClick={beginReading}>Start reading</button></div>
            </Modal>
          )}

          {eduConfirm && (
            <Modal titleId="eduConfirmTitle" onEscape={() => setEduConfirm(false)}>
              <h2 id="eduConfirmTitle">Ready for the follow-up?</h2>
              <p className="muted">Just confirm you’ve gone through the module, then we’ll see what stuck.</p>
              <label className="checkbox">
                <input type="checkbox" checked={eduUnderstood} onChange={(e) => setEduUnderstood(e.target.checked)} />
                <span>I have read and understood this learning module.</span>
              </label>
              <div className="btn-row">
                <button className="btn btn--ghost" onClick={() => setEduConfirm(false)}>Back to the module</button>
                <button className="btn btn--primary" disabled={!eduUnderstood || busy} onClick={finishEducation}>Continue to follow-up</button>
              </div>
            </Modal>
          )}
        </>
      )}

      {phase === 'done' && (
        <div className="card center">
          <div className="big-check">✓</div>
          <h1>Thank you!</h1>
          <p>Your responses have been recorded{isPrePost ? ' — and you’ve locked in the current best-practice answers' : ''}.</p>
          <div className="done-enable">
            <div className="edu-point">You’ve added a real data point to dental-education research.</div>
            <div className="edu-point">Everything is reported only as anonymous group totals.</div>
            <div className="edu-point">More responses mean stronger, more publishable findings.</div>
          </div>
          <div className="done-feedback"><FeedbackPrompt uid={uid} study={study} /></div>
          <p className="muted small">Know a classmate who’s eligible? Sharing takes the study further.</p>
          <div className="btn-row" style={{ justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
            <button className="btn btn--primary" onClick={shareStudy}>{shared ? 'Link copied ✓' : 'Share this study'}</button>
            <Link className="btn btn--ghost" to="/studies">Explore other studies</Link>
          </div>
          <div className="done-subscribe"><SubscribeCTA source="done" compact /></div>
        </div>
      )}

      <footer className="footer">
        <div className="footer__legal">KAPSTAT™ · anonymous &amp; secure · operated by Eddlynn Edtech Private Limited</div>
      </footer>
    </div>
  );
}
